import React, { Fragment, memo, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  getRoofState,
  getUIState,
  getWallState,
  getSettingsState,
} from '../../../store/slices/selectors'
import { roofActions } from '../../../store/slices'

import { FastField, Field } from 'formik'

import {
  Accordion,
  Dropdown,
  FormGroup,
  FormLabel,
  FormControl,
  RadioGroup,
  Radio,
  RatioInput,
} from '../../../../../components'

import { defaultValues, helpText } from '../../../common/json/common.json'
import {
  memberTypeOptions,
  typeOptions,
  roofThicknessOptions,
  topChordDepthOptions,
  bottomChordDepthOptions,
  endVerticalDepthOptions,
  plyOptions,
  overhangOptions,
} from '../../../common/json/carrying-member.json'
import {
  getHeelMaxHeight,
  getHeelMinHeight,
  getIJoistFlgThicknessLabel,
  getIJoistFlgWidthLabel,
  getIJoistFlgWidthValue,
  getRoofWidthOptions,
  isOneDropdownOption,
} from '../../../common/fns'
import {
  SSTHT_RAFTER,
  ROOF_MEMBER_TYPES,
  APPLICATION_TYPES,
  HEEL_TOLERANCE,
  MIN_RAFTER_HEEL,
  IJOIST_INPUT_MANUALLY_DEFAULT_WIDTH,
  IJOIST_INPUT_MANUALLY_DEFAULT_THICKNESS,
} from '../../../common/constants'

import { RenderIf, stringsToDropdownOpt } from '../../../../../utils/fns'
import {
  IJOIST_INPUT_OPTIONS,
  IJOIST_INPUT_TYPE,
  MEASURE_UNITS,
} from '../../../../../utils/constants'

const getDepthOptions = (memberTypeValue, typeValue, widthValue) => {
  if (memberTypeValue === ROOF_MEMBER_TYPES.RAFTER) {
    return typeValue === SSTHT_RAFTER.LVL_DFSP
      ? topChordDepthOptions.rafter[SSTHT_RAFTER.LVL_DFSP]
      : topChordDepthOptions.rafter[widthValue <= MIN_RAFTER_HEEL ? 'lessThan' : 'greaterThan']
  }

  return topChordDepthOptions[memberTypeValue]
}

const Roof = () => {
  const dispatch = useDispatch()

  const {
    memberType,
    type,
    width,
    ply,
    topChordDepth,
    bottomChordDepth,
    endVerticalDepth,
    overhang,
    ijoistInputType,
    manufacturer,
    series,
    model,
    thickness,
    topSlope,
    heel,
  } = useSelector(getRoofState)
  const applicationType = useSelector((state) => getWallState(state).applicationType)

  const manufacturerData = useSelector((state) => getUIState(state).manufacturerData)

  const country = useSelector((state) => getSettingsState(state).country)

  const manufacturerOptions = useMemo(
    () => stringsToDropdownOpt(manufacturerData ? Object.keys(manufacturerData) : []),
    [manufacturerData],
  )
  const seriesOptions = useMemo(
    () => stringsToDropdownOpt(manufacturerData ? Object.keys(manufacturerData[manufacturer]) : []),
    [manufacturerData, manufacturer],
  )

  const modelOptions = useMemo(
    () =>
      stringsToDropdownOpt(
        manufacturerData ? Object.keys(manufacturerData[manufacturer][series]) : [],
      ),
    [manufacturerData, manufacturer, series],
  )

  const isHeelDisabled = useMemo(() => {
    const maxHeel = getHeelMaxHeight(Number(topChordDepth), Number(topSlope))
    return (
      memberType !== ROOF_MEMBER_TYPES.TRUSS &&
      Number(Math.abs(maxHeel - Number(topChordDepth)).toFixed(3)) <= HEEL_TOLERANCE
    )
  }, [topChordDepth, topSlope, memberType])

  const widthOptions = useMemo(() => {
    const options = getRoofWidthOptions(memberType, type)

    return options
  }, [memberType, type])

  const thicknessOptions = useMemo(() => {
    const options = roofThicknessOptions[type] ?? roofThicknessOptions.default

    return options
  }, [type])

  const tcDepthOptions = useMemo(() => {
    return getDepthOptions(memberType, type, width)
  }, [memberType, type, width])

  const handleChange = (e) =>
    dispatch(roofActions.modifyCalculation({ name: e.target.name, value: e.target.value }))

  const handleChangeWidth = (e) => {
    const { name, value } = e.target
    const isDepthOptAvl = checkDepthOptAvl(memberType, type, value, topChordDepth)
    dispatch(
      roofActions.updateCalculation({
        [name]: value,
        ...(isDepthOptAvl !== true
          ? {
              topChordDepth: isDepthOptAvl.value,
            }
          : {}),
      }),
    )
  }

  const calculateHeel = (tcDepth) => {
    const maxHeel = getHeelMaxHeight(Number(tcDepth), Number(topSlope))
    if (Number(Math.abs(maxHeel - Number(tcDepth)).toFixed(3)) <= HEEL_TOLERANCE)
      return {
        topChordDepth: tcDepth,
      }
    else {
      const minHeel = getHeelMinHeight(Number(tcDepth), Number(thickness), Number(topSlope))
      let newHeel = heel
      if (memberType === ROOF_MEMBER_TYPES.RAFTER)
        newHeel = heel > maxHeel ? maxHeel : heel < MIN_RAFTER_HEEL ? MIN_RAFTER_HEEL : heel
      else if (memberType === ROOF_MEMBER_TYPES.IJOIST)
        newHeel = heel > maxHeel ? maxHeel : heel < minHeel ? minHeel : heel

      return {
        topChordDepth: tcDepth,
        heel: newHeel,
      }
    }
  }

  const handleTopChordDepthChange = (e) => {
    const data = calculateHeel(e.target.value)
    dispatch(roofActions.updateCalculation(data))
  }

  const handleChangeMemberType = (e) => {
    const { value } = e.target
    const updatedRoof =
      defaultValues.roof[value][applicationType] ?? defaultValues.roof[value].default

    let newHeelHeight = 0
    switch (value) {
      case ROOF_MEMBER_TYPES.RAFTER:
        newHeelHeight = updatedRoof.topSlope === 0 ? updatedRoof.topChordDepth : 4.5
        break
      case ROOF_MEMBER_TYPES.IJOIST:
        newHeelHeight = updatedRoof.topSlope === 0 ? updatedRoof.topChordDepth : 9.5
        break
      default:
        newHeelHeight = 4.5
        break
    }

    dispatch(
      roofActions.updateCalculation({
        ...updatedRoof,
        heel: Math.abs(Number(newHeelHeight).toFixed(1)),
      }),
    )
  }

  const checkDepthOptAvl = (updatedMemberType, updatedType, updatedWidth, updatedDepth) => {
    const depthOptions = getDepthOptions(updatedMemberType, updatedType, updatedWidth)
    const isOptAvl = depthOptions.some((o) => o.value === updatedDepth)
    return isOptAvl ? true : depthOptions.find((o) => o.default) ?? depthOptions[0]
  }

  const handleChangeLumberSpecies = (e) => {
    const { name, value, options } = e.target
    const selectedOption = options.find((opt) => opt.value === value)
    const updatedWidthOptions = getRoofWidthOptions(memberType, value)
    let updatedWidth = width
    if (memberType === ROOF_MEMBER_TYPES.IJOIST && ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY)
      updatedWidth = IJOIST_INPUT_MANUALLY_DEFAULT_WIDTH
    else {
      const widthOption = updatedWidthOptions.find((o) => o.value === width)
      updatedWidth = widthOption ? width : updatedWidthOptions.find((o) => o.default).value
    }
    const isDepthOptAvl = checkDepthOptAvl(memberType, value, updatedWidth, topChordDepth)

    dispatch(
      roofActions.updateCalculation({
        [name]: value,
        lumberSpecies: selectedOption.text,
        width: updatedWidth,
        ...(memberType === ROOF_MEMBER_TYPES.IJOIST &&
        ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY
          ? {
              thickness: IJOIST_INPUT_MANUALLY_DEFAULT_THICKNESS,
            }
          : {}),
        ...(isDepthOptAvl !== true ? { topChordDepth: isDepthOptAvl.value } : {}),
      }),
    )
  }

  const handleChangeIJoistInputType = (e) => {
    const { name, value } = e.target
    dispatch(
      roofActions.updateCalculation({
        [name]: value,
        ...(value === IJOIST_INPUT_TYPE.MANUFACTURER
          ? defaultValues.roof.ijoist[applicationType] ?? defaultValues.roof.ijoist.default
          : {}),
      }),
    )
  }

  const handleChangeManufacturer = (e) => {
    const { name, value } = e.target
    const selectedSeries = Object.keys(manufacturerData[value])[0]
    const selectedModel = Object.values(manufacturerData[value][selectedSeries])[0]

    dispatch(
      roofActions.updateInput({
        [name]: value,
        series: selectedModel.series,
        ...convertManufacturerData(selectedModel),
      }),
    )
  }

  const handleChangeSeries = (e) => {
    const { name, value } = e.target
    const selectedModel = Object.values(manufacturerData[manufacturer][value])[0]
    dispatch(
      roofActions.updateInput({
        [name]: value,
        ...convertManufacturerData(selectedModel),
      }),
    )
  }

  const handleModelChange = (e) => {
    const selectedModel = manufacturerData[manufacturer][series][e.target.value]
    dispatch(roofActions.updateInput(convertManufacturerData(selectedModel)))
  }

  const handleChangeRatioInput = (name, value) => {
    dispatch(roofActions.modifyCalculation({ name, value }))
  }

  const convertManufacturerData = (selectedModel) => {
    const { material, width, depth, flangeThickness } = selectedModel
    const memberWidth = getIJoistFlgWidthValue(width)
    const selectedOption = typeOptions.ijoist.default.find((opt) => opt.value === material)

    return {
      type: material,
      lumberSpecies: selectedOption.text,
      width: memberWidth,
      thickness: flangeThickness,
      model: selectedModel.product,
      ...calculateHeel(depth),
    }
  }

  useEffect(() => {
    if (isHeelDisabled)
      dispatch(
        roofActions.updateCalculation({
          heel: topChordDepth,
        }),
      )
  }, [topChordDepth, topSlope])

  return (
    <Accordion title="Roof (carried member)">
      <div className="grid grid__auto--200 grid--gap-1-2">
        <FormGroup>
          <FormLabel htmlFor="memberType">Roof Type</FormLabel>
          <Dropdown
            name="memberType"
            value={memberType}
            options={memberTypeOptions}
            onChange={handleChangeMemberType}
            disabled={
              isOneDropdownOption(memberTypeOptions) ||
              applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
            }
          />
        </FormGroup>
        {RenderIf(
          memberType === ROOF_MEMBER_TYPES.IJOIST,
          <FormGroup>
            <FormLabel htmlFor="ijoistInputType">Dimensions By Manufacturer Info</FormLabel>
            <RadioGroup
              name="ijoistInputType"
              className="-orange"
              onChange={handleChangeIJoistInputType}
              selectedValue={ijoistInputType}
            >
              {IJOIST_INPUT_OPTIONS.map((item) => (
                <label key={item.value} className="inputLabel">
                  <Radio value={item.value} />
                  <span>{item.text}</span>
                </label>
              ))}
            </RadioGroup>
          </FormGroup>,
        )}
        {RenderIf(
          !(
            memberType === ROOF_MEMBER_TYPES.IJOIST &&
            ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
          ),
          <FormGroup>
            <FormLabel htmlFor="type">Lumber Species</FormLabel>
            <Dropdown
              name="type"
              value={type}
              options={typeOptions[memberType][country] ?? typeOptions[memberType].default}
              onChange={handleChangeLumberSpecies}
            />
          </FormGroup>,
        )}
        {RenderIf(
          memberType === ROOF_MEMBER_TYPES.IJOIST,
          <Fragment>
            {RenderIf(
              ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER,
              <Fragment>
                <FormGroup>
                  <FormLabel htmlFor="manufacturer">Manufacturer</FormLabel>
                  <Dropdown
                    name="manufacturer"
                    value={manufacturer}
                    options={manufacturerOptions}
                    onChange={handleChangeManufacturer}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="series">Series</FormLabel>
                  <Dropdown
                    name="series"
                    value={series}
                    options={seriesOptions}
                    onChange={handleChangeSeries}
                  />
                  <p className="ijoistSeriesHelpText">
                    Flange Thickness {getIJoistFlgThicknessLabel(thickness)} & Width{' '}
                    {getIJoistFlgWidthLabel(width)}
                  </p>
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="model">Model</FormLabel>
                  <Dropdown
                    name="model"
                    value={model}
                    options={modelOptions}
                    onChange={handleModelChange}
                  />
                </FormGroup>
              </Fragment>,
            )}
            <FormGroup>
              <FormLabel htmlFor="ply">Roof Plies</FormLabel>
              <Dropdown name="ply" value={ply} options={plyOptions} onChange={handleChange} />
            </FormGroup>
            {RenderIf(
              ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY,
              <FormGroup>
                <FormLabel htmlFor="thickness">Flange Thickness</FormLabel>
                <Dropdown
                  name="thickness"
                  value={thickness}
                  options={thicknessOptions}
                  onChange={handleChange}
                  disabled={isOneDropdownOption(thicknessOptions)}
                />
              </FormGroup>,
            )}
          </Fragment>,
        )}
        {RenderIf(
          !(
            memberType === ROOF_MEMBER_TYPES.IJOIST &&
            ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
          ),
          <FormGroup>
            <FormLabel htmlFor="width">
              {memberType === ROOF_MEMBER_TYPES.IJOIST && 'Flange '}Width
            </FormLabel>
            <Dropdown
              name="width"
              value={width}
              options={widthOptions}
              onChange={handleChangeWidth}
              disabled={
                memberType === ROOF_MEMBER_TYPES.IJOIST &&
                ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
              }
            />
          </FormGroup>,
        )}
        {RenderIf(
          memberType !== ROOF_MEMBER_TYPES.IJOIST,
          <FormGroup>
            <FormLabel htmlFor="ply">Roof Plies</FormLabel>
            <Dropdown name="ply" value={ply} options={plyOptions} onChange={handleChange} />
          </FormGroup>,
        )}
        <FormGroup>
          <FormLabel htmlFor="heel">Heel Height</FormLabel>
          <Field name="heel">
            {({ field, meta }) => (
              <FormControl
                {...field}
                onChange={handleChange}
                addonAfter={MEASURE_UNITS.INCH}
                error={meta.error}
                disabled={isHeelDisabled}
              />
            )}
          </Field>
        </FormGroup>
        {RenderIf(
          memberType === ROOF_MEMBER_TYPES.TRUSS,
          <FormGroup>
            <FormLabel htmlFor="endVerticalDepth">End Vertical Depth</FormLabel>
            <Dropdown
              name="endVerticalDepth"
              value={endVerticalDepth}
              options={endVerticalDepthOptions}
              onChange={handleChange}
            />
          </FormGroup>,
        )}
        {RenderIf(
          !(
            memberType === ROOF_MEMBER_TYPES.IJOIST &&
            ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
          ),
          <FormGroup>
            <FormLabel htmlFor="topChordDepth">
              {memberType === ROOF_MEMBER_TYPES.RAFTER ||
              (memberType === ROOF_MEMBER_TYPES.IJOIST &&
                ijoistInputType === IJOIST_INPUT_TYPE.MANUALLY)
                ? 'Depth'
                : 'Top Chord Depth'}
            </FormLabel>
            <Dropdown
              name="topChordDepth"
              value={topChordDepth}
              options={tcDepthOptions}
              onChange={handleTopChordDepthChange}
              disabled={
                memberType === ROOF_MEMBER_TYPES.IJOIST &&
                ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER
              }
            />
          </FormGroup>,
        )}
        <FormGroup>
          <FormLabel htmlFor="topChordSlope">
            {memberType === ROOF_MEMBER_TYPES.TRUSS ? 'Top Chord Slope/Angle' : 'Slope/Angle'}
          </FormLabel>
          <FastField name="topSlope">
            {({ field, meta }) => (
              <RatioInput {...field} onChange={handleChangeRatioInput} error={meta.error} />
            )}
          </FastField>
        </FormGroup>
        {RenderIf(
          memberType === ROOF_MEMBER_TYPES.TRUSS,
          <FormGroup>
            <FormLabel htmlFor="bottomChordDepth">Bottom Chord Depth</FormLabel>
            <Dropdown
              name="bottomChordDepth"
              value={bottomChordDepth}
              options={bottomChordDepthOptions}
              onChange={handleChange}
            />
          </FormGroup>,
        )}
        {RenderIf(
          memberType === ROOF_MEMBER_TYPES.TRUSS,
          <FormGroup>
            <FormLabel htmlFor="bottomChordSlope">Bottom Chord Slope/Angle</FormLabel>
            <Field name="bottomSlope">
              {({ field, meta }) => (
                <RatioInput
                  {...field}
                  onChange={handleChangeRatioInput}
                  disabled={applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL}
                  error={meta.error}
                />
              )}
            </Field>
          </FormGroup>,
        )}
        <FormGroup>
          <FormLabel htmlFor="overhang" popperText={helpText.overhang}>
            Minimum 3 ½&quot; Overhang
          </FormLabel>
          <RadioGroup
            name="overhang"
            className="-orange"
            onChange={handleChange}
            selectedValue={overhang}
          >
            {overhangOptions.map((item) => (
              <label key={item.value} className="inputLabel">
                <Radio
                  value={item.value}
                  disabled={applicationType !== APPLICATION_TYPES.PERPENDICULAR_TO_WALL}
                />
                <span>{item.text}</span>
              </label>
            ))}
          </RadioGroup>
        </FormGroup>
      </div>
    </Accordion>
  )
}

Roof.propTypes = {
  country: PropTypes.string.isRequired,
}

export default memo(Roof)
